import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { AUTH_ERROR_EVENT } from '../api/api';
import localCache from '../utils/localCache';
import { STORAGE_KEYS } from '../utils/storageKeys';
import {
  BETA_FEATURES_EVENT,
  isBetaFeatureEnabled,
} from '../hooks/useBetaFeatures';
import { DEFAULT_PALETTE, PALETTE_IDS } from './palettes';

export const THEME_MODES = { LIGHT: 'light', NIGHT: 'night' };
export const FADE_CLASS = 'if-theme-fade';
export const FADE_MS = 1050;

const readMode = () =>
  localCache.get(STORAGE_KEYS.THEME_MODE) === THEME_MODES.NIGHT
    ? THEME_MODES.NIGHT
    : THEME_MODES.LIGHT;

const readPalette = () => {
  const stored = localCache.get(STORAGE_KEYS.NIGHT_PALETTE);
  return PALETTE_IDS.includes(stored) ? stored : DEFAULT_PALETTE;
};

// Same rule as the inline script in public/index.html, which paints the
// attribute before the bundle loads so a night-mode reload never flashes white.
export const resolveTheme = (mode, palette, featureEnabled) =>
  mode === THEME_MODES.NIGHT && featureEnabled ? palette : THEME_MODES.LIGHT;

const ThemeContext = createContext({
  mode: THEME_MODES.LIGHT,
  palette: DEFAULT_PALETTE,
  theme: THEME_MODES.LIGHT,
  enabled: false,
  setMode: () => {},
  setPalette: () => {},
  toggle: () => {},
});

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readMode);
  const [palette, setPaletteState] = useState(readPalette);
  const [enabled, setEnabled] = useState(() =>
    isBetaFeatureEnabled('themesManagement'),
  );

  useEffect(() => {
    const sync = () => {
      setEnabled(isBetaFeatureEnabled('themesManagement'));
      setModeState(readMode());
      setPaletteState(readPalette());
    };
    window.addEventListener(BETA_FEATURES_EVENT, sync);
    window.addEventListener(AUTH_ERROR_EVENT, sync);
    return () => {
      window.removeEventListener(BETA_FEATURES_EVENT, sync);
      window.removeEventListener(AUTH_ERROR_EVENT, sync);
    };
  }, []);

  const theme = resolveTheme(mode, palette, enabled);

  const previousTheme = useRef(theme);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    // Read by the pre-paint script in public/index.html.
    localCache.set(STORAGE_KEYS.RESOLVED_THEME, theme);
    if (previousTheme.current === theme) return undefined;
    previousTheme.current = theme;
    root.classList.add(FADE_CLASS);
    const timer = setTimeout(() => root.classList.remove(FADE_CLASS), FADE_MS);
    return () => {
      clearTimeout(timer);
      root.classList.remove(FADE_CLASS);
    };
  }, [theme]);

  const setMode = useCallback((next) => {
    const value =
      next === THEME_MODES.NIGHT ? THEME_MODES.NIGHT : THEME_MODES.LIGHT;
    localCache.set(STORAGE_KEYS.THEME_MODE, value);
    setModeState(value);
  }, []);

  const setPalette = useCallback((next) => {
    const value = PALETTE_IDS.includes(next) ? next : DEFAULT_PALETTE;
    localCache.set(STORAGE_KEYS.NIGHT_PALETTE, value);
    setPaletteState(value);
  }, []);

  const toggle = useCallback(
    () =>
      setMode(
        mode === THEME_MODES.NIGHT ? THEME_MODES.LIGHT : THEME_MODES.NIGHT,
      ),
    [mode, setMode],
  );

  const value = useMemo(
    () => ({ mode, palette, theme, enabled, setMode, setPalette, toggle }),
    [mode, palette, theme, enabled, setMode, setPalette, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
